import React, { useState, useEffect, useRef } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback } from './ui/avatar';
import { MessageSquare, Send, X, Sparkles } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      loadHistory();
    }
  }, [open]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadHistory = async () => {
    try {
      const res = await api.get('/api/ai/chat/history');
      if (res.data.length > 0) {
        setMessages(res.data.map(m => ({ role: m.role, content: m.content })));
      } else {
        setMessages([{ role: 'assistant', content: "Good morning! I'm your Hamptons Hospitality AI Assistant. I have full context on your strategy, contacts, restaurants, and spaces. Ask me anything — outreach suggestions, revenue analysis, market intelligence, or strategy advice. How can I help today?" }]);
      }
    } catch {
      setMessages([{ role: 'assistant', content: "Good morning! I'm your Hamptons Hospitality AI Assistant. How can I help you today?" }]);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);
    try {
      const res = await api.post('/api/ai/chat', { message: userMsg });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch {
      toast.error('Failed to get AI response');
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const SUGGESTIONS = [
    'What should I focus on today?',
    'How do I approach a restaurant owner?',
    'Analyze my outreach strategy',
    'What makes a strong Hamptons operator?'
  ];

  return (
    <>
      {/* Floating button */}
      <button
        className="ai-fab"
        onClick={() => setOpen(true)}
        data-testid="ai-assistant-open-button"
        title="AI Assistant"
      >
        <MessageSquare size={22} />
      </button>

      {/* Chat sheet */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-[420px] sm:w-[420px] flex flex-col p-0" style={{ backgroundColor: 'hsl(var(--card))' }}>
          <SheetHeader className="px-4 py-4 border-b border-border flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
                  <Sparkles size={16} className="text-primary-foreground" />
                </div>
                <div>
                  <SheetTitle className="text-base font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>AI Assistant</SheetTitle>
                  <p className="text-xs text-muted-foreground">Hamptons Hospitality Strategy</p>
                </div>
              </div>
              <Button variant="ghost" size="icon" className="w-8 h-8" onClick={() => setOpen(false)}>
                <X size={16} />
              </Button>
            </div>
          </SheetHeader>

          {/* Messages */}
          <ScrollArea className="flex-1 px-4 py-3">
            <div className="flex flex-col gap-3">
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} fade-in`}>
                  {msg.role === 'assistant' && (
                    <Avatar className="w-7 h-7 mr-2 mt-1 flex-shrink-0">
                      <AvatarFallback className="bg-accent text-accent-foreground text-xs">
                        <Sparkles size={12} />
                      </AvatarFallback>
                    </Avatar>
                  )}
                  <div className={msg.role === 'user' ? 'msg-user' : 'msg-assistant'}>
                    {msg.content.split('\n').map((line, j) => (
                      <span key={j}>{line}{j < msg.content.split('\n').length - 1 && <br />}</span>
                    ))}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start fade-in">
                  <Avatar className="w-7 h-7 mr-2 mt-1 flex-shrink-0">
                    <AvatarFallback className="bg-accent text-accent-foreground text-xs">
                      <Sparkles size={12} />
                    </AvatarFallback>
                  </Avatar>
                  <div className="msg-assistant flex items-center gap-1">
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Suggestions */}
          {messages.length <= 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => { setInput(s); }}
                  className="text-xs px-3 py-1.5 rounded-full border border-border bg-muted hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="px-4 py-3 border-t border-border flex-shrink-0">
            <div className="flex gap-2">
              <Textarea
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask your AI assistant..."
                className="flex-1 min-h-[44px] max-h-[120px] resize-none text-sm"
                data-testid="ai-assistant-message-input"
                rows={1}
              />
              <Button
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                size="icon"
                className="h-11 w-11 flex-shrink-0 bg-primary"
                data-testid="ai-assistant-send-button"
              >
                <Send size={15} />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5 text-center">Press Enter to send · Shift+Enter for new line</p>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
